import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, CircularProgress } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { getActiveRegions, getRegions } from '../region/regionManager';
import { GLOBAL_REGION } from '../region/RegionContext';

// Flag image from Twemoji CDN — renders consistently on all browsers/OS
const FlagImg = ({ code }) => {
  const isoCode = (code === 'UK' ? 'GB' : code).toUpperCase();
  const codePoints = [...isoCode].map(c => (0x1F1E6 + c.charCodeAt(0) - 65).toString(16)).join('-');
  return (
    <img
      src={`https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/${codePoints}.png`}
      alt={`${isoCode} flag`}
      width={20}
      height={20}
      style={{ verticalAlign: 'middle' }}
    />
  );
};

const CountryTabs = ({ activeRegion, onRegionChange, adminRegions }) => {
  const [activeRegions, setActiveRegions] = useState(['AU']);
  const [loading, setLoading] = useState(true);
  const regions = getRegions();

  useEffect(() => {
    let mounted = true;
    getActiveRegions().then((codes) => {
      if (mounted) {
        setActiveRegions(codes);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  // Filter regions by admin permissions (if set). Unrestricted admins see all.
  const visibleRegions = adminRegions
    ? activeRegions.filter((code) => adminRegions.includes(code))
    : activeRegions;

  // Sort by geographic proximity — AU first (home), then radiating outward.
  // Approximate longitude for each region so nearby countries cluster together.
  const COUNTRY_LON = {
    AU: 134, NZ: 174, FJ: 178, SG: 104, MY: 102, ID: 118, PH: 122,
    TH: 101, VN: 108, KH: 105, TW: 121, HK: 114, CN: 105, JP: 140,
    KR: 127, IN: 79, LK: 81, MV: 73, AE: 54, QA: 51, SA: 45, IL: 35,
    EG: 31, KE: 37, NG: 8, ZA: 25, MA: -6, TR: 35, GR: 22, IT: 12,
    CH: 8, AT: 14, DE: 10, CZ: 15, PL: 20, NL: 5, DK: 10, SE: 15,
    FR: 2, ES: -4, PT: -9, IE: -8, UK: -2, BR: -48, AR: -64, CL: -71,
    PE: -76, CO: -74, CR: -84, MX: -102, CA: -96, US: -98,
  };
  // Distance from AU (lon 134) wrapping around the dateline
  const auLon = 134;
  const sortedRegions = [...visibleRegions].sort((a, b) => {
    if (a === 'AU') return -1;
    if (b === 'AU') return 1;
    const distA = Math.min(Math.abs((COUNTRY_LON[a] ?? 0) - auLon), 360 - Math.abs((COUNTRY_LON[a] ?? 0) - auLon));
    const distB = Math.min(Math.abs((COUNTRY_LON[b] ?? 0) - auLon), 360 - Math.abs((COUNTRY_LON[b] ?? 0) - auLon));
    return distA - distB;
  });

  // Build the full tab list: Global first (only for unrestricted admins), then country tabs
  const showGlobal = !adminRegions; // unrestricted admins get Global tab
  const tabList = showGlobal
    ? [GLOBAL_REGION, ...sortedRegions]
    : sortedRegions;

  const tabIndex = tabList.indexOf(activeRegion);

  return (
    <Box sx={{ borderBottom: 2, borderColor: 'primary.main', bgcolor: 'grey.50' }}>
      <Tabs
        value={tabIndex >= 0 ? tabIndex : 0}
        onChange={(_, idx) => onRegionChange(tabList[idx])}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 2,
          '& .MuiTab-root': { minHeight: 48, fontWeight: 600 },
          '& .Mui-selected': { color: 'primary.main' },
        }}
      >
        {tabList.map((code) =>
          code === GLOBAL_REGION ? (
            <Tab
              key={GLOBAL_REGION}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PublicIcon sx={{ fontSize: 20 }} />
                  <span>Global</span>
                </Box>
              }
            />
          ) : (
            <Tab
              key={code}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FlagImg code={code} />
                  <span>{regions[code]?.country || code}</span>
                </Box>
              }
            />
          )
        )}
      </Tabs>
    </Box>
  );
};

export default CountryTabs;
